import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { uniq } from "lodash";
import { LayerReferenceConfigurationDc } from "@evergis/api";
import { Divider, Tooltip } from "@evergis/ui";

import { AttributeFieldTitle } from "./AttibuteFieldTitle";
import { AttributeLabel, FieldValue, ValueLink } from "../../../styled";
import { HandBookLink, HandBookLinkContainer, HandBookLinkItem, HandBookLinkSmall } from "./styled";
import { layerInfos } from "../../../../../resources/layers";
import { setSourceAttributeName, setSourceLayerInfo } from "../../../../../ducks/showFeature/showFeature";
import { formatAttributeValue } from "../../../../../utils/formatAttributeValue";
import { useNotification } from "../../../../../hooks/notifications/useNotification";
import { FeatureAttribute } from "../FeatureAttributeField";
import { LayerInfo } from "../../../../../interfaces/resourceInfo";

type AttributeHandBookProps = {
  layer: LayerInfo;
  attribute: FeatureAttribute;
  showLabel?: boolean;
  setHandBookReference: (reference: LayerReferenceConfigurationDc) => void;
};

export const AttributeHandBook: FC<AttributeHandBookProps> = ({
  layer,
  attribute,
  showLabel = true,
  setHandBookReference,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { value, type, alias, name, stringFormat, description } = attribute;
  const valueWithFormatSettings = formatAttributeValue(type, value, stringFormat);
  const title = alias || name;

  const fieldTitle = useMemo(
    () => <AttributeFieldTitle title={title} description={description} />,
    [title, description],
  );

  const layerAttributesReferences = useMemo((): LayerReferenceConfigurationDc[] => {
    const attributeConfiguration = layer?.configuration?.attributesConfiguration?.attributes.find(
      ({ attributeName }) => attributeName === name,
    );
    const referenceIds = attributeConfiguration?.layerReferenceId;
    const configurationLayerReferences = layer?.configuration?.attributesConfiguration?.layerReferences;

    return configurationLayerReferences?.filter(({ referenceId }) => referenceIds?.includes(referenceId));
  }, [layer, name]);

  const layerConfigReferences = useMemo((): LayerReferenceConfigurationDc[] => {
    const attributeConfig = layer.configuration.cardConfiguration?.children?.find(
      ({ attributeName, options }) => attributeName === name && options?.relatedAttributes?.length,
    );

    if (!attributeConfig) {
      return null;
    }

    return attributeConfig.options.relatedAttributes.map(({ layerName, attributeName, condition }) => ({
      layerName,
      targetAttribute: attributeConfig.attributeName,
      referenceAttribute: attributeName,
      condition,
    }));
  }, [name, layer.configuration.cardConfiguration?.children]);

  const layerReferences = layerConfigReferences || layerAttributesReferences;

  const onClickLink = useCallback(
    (reference: LayerReferenceConfigurationDc) => {
      dispatch(setSourceAttributeName(reference.targetAttribute));
      dispatch(setSourceLayerInfo(layer));
      setHandBookReference(reference);
    },
    [dispatch, layer, setHandBookReference],
  );

  const { data: referenceLayers, error: referenceLayersError } = layerInfos.use({
    input: uniq(layerReferences?.map(({ layerName }) => layerName)),
    waitInput: true,
  });

  useNotification(referenceLayersError, {
    error: true,
    title: t("dictionariesLoadingError"),
  });

  return (
    <>
      {layerReferences?.length === 1 && (
        <>
          {showLabel && <AttributeLabel>{fieldTitle}</AttributeLabel>}
          <ValueLink as={"div"}>
            <Tooltip
              placement={"top"}
              arrow
              content={`${t("relatedRecords")} ${t("of", { ns: "table" })} ${
                (referenceLayers && referenceLayers[0]?.alias) || layerReferences[0].layerName
              }`}
            >
              {ref => (
                <HandBookLink ref={ref} onClick={() => onClickLink(layerReferences[0])}>
                  {valueWithFormatSettings}
                </HandBookLink>
              )}
            </Tooltip>
          </ValueLink>
        </>
      )}
      {layerReferences?.length > 1 && (
        <>
          <FieldValue style={{ marginBottom: "0.25rem" }} field={fieldTitle} value={valueWithFormatSettings} />
          <HandBookLinkContainer>
            {layerReferences.map((reference, index) => {
              const referenceLayer = referenceLayers?.find(
                ({ name: refLayerName }) => refLayerName === reference.layerName,
              );
              const layerName = referenceLayer ? referenceLayer.alias || reference.layerName : reference.layerName;
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const layerAttributesDefinition = referenceLayer && referenceLayer?.layerDefinition?.attributes;
              const attributeDefinition =
                layerAttributesDefinition && layerAttributesDefinition[reference.referenceAttribute];
              const attributeName = attributeDefinition
                ? attributeDefinition.alias || reference.referenceAttribute
                : reference.referenceAttribute;

              return (
                <HandBookLinkItem>
                  <HandBookLinkSmall onClick={() => onClickLink(reference)}>
                    {`${layerName} (${attributeName})`}
                  </HandBookLinkSmall>
                  {index !== layerReferences.length - 1 && (
                    <Divider style={{ marginLeft: "0.5rem", marginRight: "0.5rem", width: "auto" }} vertical />
                  )}
                </HandBookLinkItem>
              );
            })}
          </HandBookLinkContainer>
        </>
      )}
    </>
  );
};
